import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import PermButton from "@/components/PermButton";
import { toast } from "sonner";
import { Loader2, PackagePlus, RotateCcw, AlertTriangle, Eye, Plus, X } from "lucide-react";
import useAuthStore from "@/store/authStore";
import { RMSearchSelect } from "@/components/bill/RMSearchSelect";
import { resolveTypedRM, filterRMsForSearch } from "@/lib/rmResolver";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job Work Lifecycle Phase 2/3 — Receipt (goods received back from vendor
// with conversion done), plus Scrap declaration + Return (unconverted
// material) as in-page dialogs. Mirrors the RMProduction preview-then-
// commit pattern (single page + preview dialog before write).
export default function JobWorkReceipts() {
  const { token } = useAuthStore();
  const h = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── List state ────────────────────────────────────────────────────────
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rms, setRms] = useState([]);
  const [flt, setFlt] = useState({ vendor_id: "ALL", branch: "ALL", rm_id: "", date_from: "", date_to: "" });

  // ── Dialogs ───────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [detailReceipt, setDetailReceipt] = useState(null);
  const [showScrap, setShowScrap] = useState(false);
  const [vendorBalances, setVendorBalances] = useState([]);

  // ── Create form ───────────────────────────────────────────────────────
  // Feb 2026: `linked_dc_ids` no longer captured from UI (receipts are
  // decoupled from DCs). Payload keeps the field as `[]` for backend
  // schema compatibility.
  // Aug 2026 (ledger collapse) — `job_worker_stock` is one balance per
  // (vendor_id, rm_id); the Create dialog no longer prompts for an
  // origin branch. `branch` is the receiving branch (finished-goods
  // stock credit). Return/Scrap dialogs continue to carry their own
  // `origin_branch` field for physical audit context only.
  const empty = { branch: "", vendor_id: "", rm_id: "", rm_search: "", quantity_received: "",
    regrind_feeds: [], target_poly_rm_id: "", jobwork_invoice_ref: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);

  // ── Return form ───────────────────────────────────────────────────────
  const [retForm, setRetForm] = useState({ vendor_id: "", origin_branch: "", rm_id: "", qty: "", notes: "" });
  const [returning, setReturning] = useState(false);

  // ── Scrap form ────────────────────────────────────────────────────────
  // Standalone (Feb 2026): not tied to any receipt. `ledger_key` is the
  // composite value used by the RM picker's <SelectItem> so a single
  // selection resolves both `rm_id` and `origin_branch`.
  const [scrapForm, setScrapForm] = useState({ vendor_id: "", origin_branch: "", rm_id: "", ledger_key: "", qty: "", notes: "" });
  const [scrapping, setScrapping] = useState(false);

  // ── Bootstrap ─────────────────────────────────────────────────────────
  const bootstrap = useCallback(async () => {
    try {
      const [v, b, r] = await Promise.all([
        axios.get(`${API}/vendors?is_job_worker=true`, { headers: h() }),
        axios.get(`${API}/branches`, { headers: h() }).catch(() => ({ data: [] })),
        axios.get(`${API}/raw-materials?limit=5000`, { headers: h() }).catch(() => ({ data: [] })),
      ]);
      setVendors(Array.isArray(v.data) ? v.data : []);
      const bd = Array.isArray(b.data) ? b.data : (b.data?.items || []);
      setBranches(bd);
      const rd = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setRms(rd);
    } catch (_e) { /* filter dropdowns can gracefully stay empty */ }
  }, [h]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (flt.vendor_id !== "ALL") p.set("vendor_id", flt.vendor_id);
      if (flt.branch !== "ALL") p.set("branch", flt.branch);
      if (flt.rm_id) p.set("finished_rm_id", flt.rm_id.toUpperCase());
      if (flt.date_from) p.set("date_from", flt.date_from);
      if (flt.date_to) p.set("date_to", flt.date_to);
      p.set("limit", "200");
      const res = await axios.get(`${API}/jobwork/receipts?${p.toString()}`, { headers: h() });
      setReceipts(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load receipts");
    } finally { setLoading(false); }
  }, [flt, h]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const submitPreview = async () => {
    // Feb 2026: field-level error surfacing so users know exactly what to
    // fix — previously a single generic toast fired even when e.g. only
    // the Finished RM auto-confirm had silently missed.
    const missing = [];
    if (!form.branch) missing.push("Branch");
    if (!form.vendor_id) missing.push("Vendor");
    if (!(form.rm_id || "").trim()) missing.push("Finished RM");
    if (!form.quantity_received) missing.push("Quantity Received");
    if (missing.length) {
      return toast.error(`Fill required field${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`);
    }
    setPreviewing(true);
    try {
      // Aug 2026 (ledger collapse) — no origin_branch on the request;
      // backend resolves the single vendor balance.
      const body = {
        branch: form.branch,
        vendor_id: form.vendor_id,
        rm_id: form.rm_id.toUpperCase(),
        quantity_received: parseFloat(form.quantity_received),
        linked_dc_ids: [],
        regrind_feeds: form.regrind_feeds.filter(f => f.rm_id && f.pct > 0)
          .map(f => ({ rm_id: f.rm_id.toUpperCase(), pct: parseFloat(f.pct) })),
        target_poly_rm_id: form.target_poly_rm_id ? form.target_poly_rm_id.toUpperCase() : null,
        jobwork_invoice_ref: form.jobwork_invoice_ref || null,
        notes: form.notes || null,
      };
      const res = await axios.post(`${API}/jobwork/receipt/preview`, body, { headers: h() });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const submitCommit = async () => {
    if (!preview) return toast.error("Preview required before commit");
    setCommitting(true);
    try {
      const body = {
        branch: form.branch,
        vendor_id: form.vendor_id,
        rm_id: form.rm_id.toUpperCase(),
        quantity_received: parseFloat(form.quantity_received),
        linked_dc_ids: [],
        regrind_feeds: form.regrind_feeds.filter(f => f.rm_id && f.pct > 0)
          .map(f => ({ rm_id: f.rm_id.toUpperCase(), pct: parseFloat(f.pct) })),
        target_poly_rm_id: form.target_poly_rm_id ? form.target_poly_rm_id.toUpperCase() : null,
        jobwork_invoice_ref: form.jobwork_invoice_ref || null,
        notes: form.notes || null,
      };
      const res = await axios.post(`${API}/jobwork/receipt`, body, { headers: h() });
      toast.success(`Receipt ${res.data?.receipt_code} created`);
      setShowCreate(false); setForm(empty); setPreview(null); fetchList();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Commit failed"));
    } finally { setCommitting(false); }
  };

  const submitReturn = async () => {
    if (!retForm.vendor_id || !retForm.origin_branch || !retForm.rm_id || !retForm.qty)
      return toast.error("Fill vendor, origin branch, RM, qty");
    setReturning(true);
    try {
      await axios.post(`${API}/jobwork/return`, {
        vendor_id: retForm.vendor_id, origin_branch: retForm.origin_branch,
        rm_id: retForm.rm_id.toUpperCase(), qty: parseFloat(retForm.qty),
        notes: retForm.notes || null,
      }, { headers: h() });
      toast.success("Return recorded");
      setShowReturn(false); setRetForm({ vendor_id: "", origin_branch: "", rm_id: "", qty: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Return failed");
    } finally { setReturning(false); }
  };

  const openDetail = async (code) => {
    try {
      const res = await axios.get(`${API}/jobwork/receipt/${code}`, { headers: h() });
      // Backend returns { receipt: {...}, reconciliation: [...] }. Flatten the
      // receipt to the top level so the JSX below can read fields as-is, and
      // hang the reconciliation rows off the same object.
      setDetailReceipt({ ...(res.data.receipt || {}), reconciliation: res.data.reconciliation || [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load detail"); }
  };

  const submitScrap = async () => {
    if (!scrapForm.vendor_id || !scrapForm.rm_id || !scrapForm.origin_branch || !scrapForm.qty)
      return toast.error("Pick vendor, RM (with balance) and enter qty");
    setScrapping(true);
    try {
      await axios.post(`${API}/jobwork/scrap`, {
        vendor_id: scrapForm.vendor_id,
        origin_branch: scrapForm.origin_branch,
        rm_id: scrapForm.rm_id.toUpperCase(),
        qty: parseFloat(scrapForm.qty),
        notes: scrapForm.notes || null,
      }, { headers: h() });
      toast.success("Scrap logged");
      setShowScrap(false);
      setScrapForm({ vendor_id: "", origin_branch: "", rm_id: "", ledger_key: "", qty: "", notes: "" });
      setVendorBalances([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrap declaration failed");
    } finally { setScrapping(false); }
  };

  // Fetch vendor's non-zero ledger rows for the RM picker. Same endpoint
  // that powers the Job Work Balances view — default filters out
  // balance_qty <= 1e-9, so zero rows never surface.
  const loadVendorBalances = async (vendor_id) => {
    setVendorBalances([]);
    setScrapForm(f => ({ ...f, vendor_id, origin_branch: "", rm_id: "", ledger_key: "" }));
    if (!vendor_id) return;
    try {
      const res = await axios.get(`${API}/jobwork/balances/${vendor_id}`, { headers: h() });
      setVendorBalances(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load vendor balances");
    }
  };

  const rmName = useMemo(() => Object.fromEntries((rms || []).map(r => [r.rm_id, r.name])), [rms]);
  const vendorName = useMemo(() => Object.fromEntries((vendors || []).map(v => [v.id, v.name])), [vendors]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="jobwork-receipts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <PackagePlus className="w-7 h-7 text-orange-500" />
            Job Work Receipts
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Record finished-part receipts from job-worker vendors. Debits `job_worker_stock`
            per BIDSO-supplied input, credits branch RM inventory with the finished part.
          </p>
        </div>
        <div className="flex gap-2">
          <PermButton entity="ProductionBatch" action="CREATE" onClick={() => setShowReturn(true)} variant="outline" data-testid="jw-return-btn">
            <RotateCcw className="w-4 h-4 mr-2" />Record Return
          </PermButton>
          <PermButton entity="ProductionBatch" action="CREATE" onClick={() => {
            setScrapForm({ vendor_id: "", origin_branch: "", rm_id: "", ledger_key: "", qty: "", notes: "" });
            setVendorBalances([]);
            setShowScrap(true);
          }} variant="outline" data-testid="jw-log-scrap-btn">
            <AlertTriangle className="w-4 h-4 mr-2" />Log Scrap
          </PermButton>
          <PermButton entity="ProductionBatch" action="CREATE" onClick={() => setShowCreate(true)} data-testid="jw-create-btn">
            <Plus className="w-4 h-4 mr-2" />New Receipt
          </PermButton>
        </div>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div><Label className="text-xs">Vendor</Label>
          <Select value={flt.vendor_id} onValueChange={(v) => setFlt({ ...flt, vendor_id: v })}>
            <SelectTrigger data-testid="flt-vendor"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent></Select></div>
        <div><Label className="text-xs">Branch</Label>
          <Select value={flt.branch} onValueChange={(v) => setFlt({ ...flt, branch: v })}>
            <SelectTrigger data-testid="flt-branch"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All branches</SelectItem>
              {branches.map(b => <SelectItem key={b.name || b} value={b.name || b}>{b.name || b}</SelectItem>)}
            </SelectContent></Select></div>
        <div><Label className="text-xs">Finished RM ID</Label>
          <Input value={flt.rm_id} onChange={e => setFlt({ ...flt, rm_id: e.target.value })} placeholder="RM_..." data-testid="flt-rm" /></div>
        <div><Label className="text-xs">From</Label>
          <Input type="date" value={flt.date_from} onChange={e => setFlt({ ...flt, date_from: e.target.value })} /></div>
        <div><Label className="text-xs">To</Label>
          <Input type="date" value={flt.date_to} onChange={e => setFlt({ ...flt, date_to: e.target.value })} /></div>
      </CardContent></Card>

      {/* List */}
      <Card><CardHeader className="pb-3"><CardTitle className="text-base">Receipts ({receipts.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          : receipts.length === 0 ? <div className="p-8 text-center text-zinc-500" data-testid="jw-receipts-empty">No receipts match these filters.</div>
          : <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr>
                <th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Vendor</th><th className="px-4 py-3 text-left">Branch</th>
                <th className="px-4 py-3 text-left">Finished RM</th><th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Invoice Ref</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>{receipts.map(r => (
                <tr key={r.receipt_code} className="border-t hover:bg-zinc-50" data-testid={`receipt-row-${r.receipt_code}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.receipt_code}</td>
                  <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">{vendorName[r.vendor_id] || r.vendor_id}</td>
                  <td className="px-4 py-3 text-xs">{r.branch}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.finished_rm_id}<div className="text-[11px] text-zinc-500 font-sans">{rmName[r.finished_rm_id] || ""}</div></td>
                  <td className="px-4 py-3 font-mono text-right">{Number(r.quantity_received || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-xs">{r.jobwork_invoice_ref || "—"}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(r.receipt_code)} data-testid={`view-${r.receipt_code}`}><Eye className="w-4 h-4" /></Button></td>
                </tr>))}</tbody>
            </table>}
        </CardContent></Card>

      {/* ── Create dialog ────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) { setForm(empty); setPreview(null); }}}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Job Work Receipt</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Receiving Branch <span className="text-zinc-400">(stock credited)</span></Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger data-testid="new-branch"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.name || b} value={b.name || b}>{b.name || b}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Vendor (Job Worker)</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="new-vendor"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-xs">Finished RM</Label>
              <Input
                value={form.rm_search || form.rm_id}
                onChange={(e) => {
                  const v = e.target.value;
                  const resolved = resolveTypedRM(v, rms, { includeFreight: false });
                  setForm((prev) => ({
                    ...prev,
                    rm_search: v,
                    ...(resolved ? { rm_id: resolved.rm_id } : {}),
                  }));
                }}
                placeholder="Type RM ID or description…"
                className="font-mono text-xs h-9"
                list="new-rm-datalist"
                data-testid="new-rm-input"
              />
              <datalist id="new-rm-datalist">
                {filterRMsForSearch(form.rm_search || "", rms, { includeFreight: false }).map((rm) => (
                  <option key={rm.rm_id} value={rm.rm_id}>
                    {(rm.category_data && rm.category_data.part_name) || rm.description || rm.name || ""}
                  </option>
                ))}
              </datalist>
              {form.rm_search && !form.rm_id && (
                <select
                  className="w-full text-xs border rounded px-1 py-1 bg-gray-50 mt-1"
                  value=""
                  onChange={(e) => {
                    const rmId = e.target.value;
                    if (!rmId) return;
                    setForm((prev) => ({ ...prev, rm_id: rmId, rm_search: rmId }));
                  }}
                  data-testid="new-rm-picker"
                >
                  <option value="">
                    {filterRMsForSearch(form.rm_search || "", rms, { includeFreight: false }).length === 0
                      ? "No matches in RM catalog"
                      : "Select from matches"}
                  </option>
                  {filterRMsForSearch(form.rm_search || "", rms, { includeFreight: false }).map((rm) => (
                    <option key={rm.rm_id} value={rm.rm_id}>
                      {rm.rm_id}
                      {((rm.category_data && rm.category_data.part_name) || rm.description)
                        ? ` — ${(rm.category_data && rm.category_data.part_name) || rm.description}`
                        : ""}
                    </option>
                  ))}
                </select>
              )}
              {form.rm_id && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[11px] text-zinc-600">
                  <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                    Selected
                  </span>
                  <span className="font-mono">{form.rm_id}</span>
                </div>
              )}
            </div>
            <div><Label className="text-xs">Quantity Received</Label>
              <Input type="number" value={form.quantity_received} onChange={e => setForm({ ...form, quantity_received: e.target.value })} data-testid="new-qty" /></div>
            <div><Label className="text-xs">Target POLY (regrind blend)</Label>
              <Input value={form.target_poly_rm_id} onChange={e => setForm({ ...form, target_poly_rm_id: e.target.value })} placeholder="Optional" /></div>
            <div><Label className="text-xs">Invoice Ref</Label>
              <Input value={form.jobwork_invoice_ref} onChange={e => setForm({ ...form, jobwork_invoice_ref: e.target.value })} placeholder="Optional" /></div>
            <div className="col-span-2"><Label className="text-xs">Regrind Feeds</Label>
              <div className="space-y-1">{form.regrind_feeds.map((f, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="flex-1">
                    <RMSearchSelect
                      value={f.rm_id}
                      onChange={(rmId) => { const n=[...form.regrind_feeds]; n[i].rm_id=rmId; setForm({...form, regrind_feeds:n}); }}
                      items={rms}
                      placeholder="Regrind RM ID"
                      emptyHint="No matches"
                      testIdPrefix={`new-regrind-${i}`}
                    />
                  </div>
                  <Input className="text-xs w-24" type="number" value={f.pct} placeholder="%" onChange={e => { const n=[...form.regrind_feeds]; n[i].pct=e.target.value; setForm({...form, regrind_feeds:n}); }} />
                  <Button size="sm" variant="ghost" onClick={() => setForm({...form, regrind_feeds: form.regrind_feeds.filter((_,idx)=>idx!==i)})}><X className="w-3 h-3" /></Button>
                </div>))}
                <Button size="sm" variant="outline" onClick={() => setForm({...form, regrind_feeds:[...form.regrind_feeds, {rm_id:"", pct:""}]})}>+ Feed</Button>
              </div></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          {preview && (
            <div className="border rounded p-3 bg-zinc-50 mt-2 text-xs space-y-2" data-testid="preview-panel">
              <div className="font-semibold">Preview
                {(preview.reconciliation_exceptions?.length > 0)
                  ? <span className="text-red-700 ml-2" data-testid="preview-recon-badge">
                      ⚠ Reconciliation exception — BIDSO-supplied component short in ledger
                    </span>
                  : <span className="text-emerald-700 ml-2">✓ Full debit possible</span>}
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500">
                  <th className="text-left">RM</th>
                  <th className="text-left">Owner</th>
                  <th className="text-right">Required</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">Will Debit</th>
                  <th className="text-right">Shortfall</th>
                </tr></thead>
                <tbody>{(preview.consumed_lines || []).map((c, i) => (
                  <tr key={c.rm_id || i} className="font-mono">
                    <td>
                      {c.rm_id}
                      {c.mb_substitution_applied && (
                        <span
                          className="ml-1 px-1 rounded bg-sky-50 border border-sky-200 text-sky-700 text-[9px]"
                          title={`BOM row ${c.bom_rm_id} → vendor-ledger sibling ${c.rm_id}`}
                          data-testid={`mb-sub-badge-${c.rm_id}`}
                        >
                          MB sub
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`text-[10px] px-1 rounded ${
                        c.ownership === "VENDOR_SUPPLIED"
                          ? "bg-amber-50 border border-amber-200 text-amber-700"
                          : "bg-zinc-100 border border-zinc-200 text-zinc-700"
                      }`}>
                        {c.ownership === "VENDOR_SUPPLIED" ? "VENDOR" : "BIDSO"}
                      </span>
                    </td>
                    <td className="text-right">{Number(c.required_qty ?? c.quantity ?? 0).toLocaleString("en-IN", {maximumFractionDigits:3})}</td>
                    <td className="text-right text-zinc-600">{Number(c.ledger_before ?? 0).toLocaleString("en-IN", {maximumFractionDigits:3})}</td>
                    <td className="text-right">{Number(c.debit_qty ?? 0).toLocaleString("en-IN", {maximumFractionDigits:3})}</td>
                    <td className={`text-right ${c.is_reconciliation_exception ? "text-red-700 font-semibold" : c.shortfall > 0 ? "text-amber-700" : "text-zinc-400"}`}>
                      {Number(c.shortfall || 0).toLocaleString("en-IN", {maximumFractionDigits:3})}
                    </td>
                  </tr>))}</tbody>
              </table>
              {preview.skipped_vendor_supplied?.length > 0 && (
                <div className="text-[11px] text-amber-700 border-t pt-2">
                  {preview.skipped_vendor_supplied.length} VENDOR-owned component(s) excluded from
                  the debit (per linked DC).
                </div>
              )}
              {preview.reconciliation_exceptions?.length > 0 && (
                <div className="text-[11px] text-red-700 border-t border-red-200 pt-2" data-testid="preview-recon-detail">
                  <div className="font-semibold">Reconciliation required:</div>
                  {preview.reconciliation_exceptions.map((e) => (
                    <div key={e.rm_id} className="font-mono">
                      {e.rm_id}: needed {Number(e.required_qty).toLocaleString("en-IN", {maximumFractionDigits:3})},
                      ledger has {Number(e.ledger_balance).toLocaleString("en-IN", {maximumFractionDigits:3})} —
                      short {Number(e.shortfall).toLocaleString("en-IN", {maximumFractionDigits:3})}.
                    </div>
                  ))}
                  <div className="mt-1">
                    BIDSO-owned material short in the vendor ledger — investigate the DC / consumption
                    mismatch before committing. The receipt will still commit and debit what&apos;s available,
                    but the shortfall will NOT be reclassified as vendor-supplied.
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="outline" onClick={submitPreview} disabled={previewing} data-testid="btn-preview">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
            </Button>
            <PermButton entity="ProductionBatch" action="CREATE" onClick={submitCommit}
              disabled={!preview || committing} data-testid="btn-commit">
              {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Commit"}
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Return dialog ────────────────────────────────────────────── */}
      <Dialog open={showReturn} onOpenChange={setShowReturn}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Return (Unconverted Material)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Vendor</Label>
              <Select value={retForm.vendor_id} onValueChange={(v) => setRetForm({...retForm, vendor_id:v})}>
                <SelectTrigger data-testid="ret-vendor"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Origin Branch</Label>
              <Select value={retForm.origin_branch} onValueChange={(v) => setRetForm({...retForm, origin_branch:v})}>
                <SelectTrigger data-testid="ret-branch"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.name || b} value={b.name || b}>{b.name || b}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">RM ID</Label>
              <RMSearchSelect
                value={retForm.rm_id}
                onChange={(rmId) => setRetForm({...retForm, rm_id: rmId})}
                items={rms}
                placeholder="Type RM ID or description…"
                emptyHint="No matches in RM catalog"
                testIdPrefix="ret-rm"
              /></div>
            <div><Label className="text-xs">Quantity</Label>
              <Input type="number" value={retForm.qty} onChange={e => setRetForm({...retForm, qty:e.target.value})} data-testid="ret-qty" /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Textarea value={retForm.notes} onChange={e => setRetForm({...retForm, notes:e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturn(false)}>Cancel</Button>
            <PermButton entity="ProductionBatch" action="CREATE" onClick={submitReturn} disabled={returning} data-testid="btn-return">
              {returning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Return"}
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog ────────────────────────────────────────────── */}
      <Dialog open={!!detailReceipt} onOpenChange={(o) => !o && setDetailReceipt(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detailReceipt?.receipt_code}</DialogTitle></DialogHeader>
          {detailReceipt && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-zinc-500">Vendor:</span> {vendorName[detailReceipt.vendor_id] || detailReceipt.vendor_id}</div>
                <div><span className="text-zinc-500">Branch:</span> {detailReceipt.branch}</div>
                <div><span className="text-zinc-500">Finished RM:</span> {detailReceipt.finished_rm_id}</div>
                <div><span className="text-zinc-500">Qty Received:</span> {Number(detailReceipt.quantity_received || 0).toLocaleString("en-IN")}</div>
                <div><span className="text-zinc-500">Invoice Ref:</span> {detailReceipt.jobwork_invoice_ref || "—"}</div>
                <div><span className="text-zinc-500">Notes:</span> {detailReceipt.notes || "—"}</div>
              </div>
              <div className="border-t pt-2">
                <div className="font-semibold mb-1">Per-Input Reconciliation</div>
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50"><tr>
                    <th className="p-2 text-left">RM</th><th className="p-2 text-right">Ideal</th>
                    <th className="p-2 text-right">Before</th><th className="p-2 text-right">After</th>
                  </tr></thead>
                  <tbody>{(detailReceipt.reconciliation || []).map((c, i) => (
                    <tr key={c.rm_id || i} className="border-t" data-testid={`detail-recon-row-${c.rm_id}`}><td className="p-2 font-mono">{c.rm_id}</td>
                      <td className="p-2 font-mono text-right">{Number(c.ideal_consumed || 0).toLocaleString("en-IN")}</td>
                      <td className="p-2 font-mono text-right">{c.ledger_before_at_commit != null ? Number(c.ledger_before_at_commit).toLocaleString("en-IN") : "—"}</td>
                      <td className="p-2 font-mono text-right">{c.ledger_after_at_commit != null ? Number(c.ledger_after_at_commit).toLocaleString("en-IN") : "—"}</td>
                    </tr>))}</tbody>
                </table>
              </div>
              {(detailReceipt.scrap_declared_actions?.length > 0) && (
                <div className="border-t pt-2">
                  <div className="font-semibold mb-1">Scrap Declared</div>
                  {detailReceipt.scrap_declared_actions.map((s, i) => (
                    <div key={i} className="text-xs flex justify-between"><span className="font-mono">{s.rm_id}</span><span>{s.qty}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Log Scrap dialog (standalone — not tied to any receipt) ──── */}
      <Dialog open={showScrap} onOpenChange={(o) => {
        setShowScrap(o);
        if (!o) {
          setScrapForm({ vendor_id: "", origin_branch: "", rm_id: "", ledger_key: "", qty: "", notes: "" });
          setVendorBalances([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Log Scrap</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Vendor</Label>
              <Select value={scrapForm.vendor_id} onValueChange={loadVendorBalances}>
                <SelectTrigger data-testid="scrap-vendor"><SelectValue placeholder="Select vendor…" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-xs">RM (only nonzero balances shown)</Label>
              <Select
                value={scrapForm.ledger_key}
                onValueChange={(key) => {
                  const row = vendorBalances.find(r => `${r.rm_id}||${r.origin_branch}` === key);
                  setScrapForm(f => ({
                    ...f,
                    ledger_key: key,
                    rm_id: row?.rm_id || "",
                    origin_branch: row?.origin_branch || "",
                  }));
                }}
                disabled={!scrapForm.vendor_id}
              >
                <SelectTrigger data-testid="scrap-rm">
                  <SelectValue placeholder={scrapForm.vendor_id
                    ? (vendorBalances.length ? "Select RM…" : "No nonzero balance for this vendor")
                    : "Pick a vendor first"} />
                </SelectTrigger>
                <SelectContent>
                  {vendorBalances.map(r => (
                    <SelectItem key={`${r.rm_id}||${r.origin_branch}`} value={`${r.rm_id}||${r.origin_branch}`}
                      data-testid={`scrap-ledger-${r.rm_id}`}>
                      <span className="font-mono">{r.rm_id}</span>
                      <span className="text-zinc-500"> · {r.origin_branch}</span>
                      <span className="text-zinc-500"> · Bal: {Number(r.balance_qty || 0).toLocaleString("en-IN")} {r.uom || ""}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Quantity</Label>
              <Input type="number" value={scrapForm.qty}
                onChange={e => setScrapForm({...scrapForm, qty: e.target.value})}
                data-testid="scrap-qty" /></div>
            <div><Label className="text-xs">Origin Branch</Label>
              <Input value={scrapForm.origin_branch} readOnly disabled data-testid="scrap-branch-readonly" /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Textarea value={scrapForm.notes}
                onChange={e => setScrapForm({...scrapForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScrap(false)}>Cancel</Button>
            <PermButton entity="ProductionBatch" action="CREATE" onClick={submitScrap} disabled={scrapping} data-testid="btn-scrap-commit">
              {scrapping ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log Scrap"}
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
